import React, { useState, useEffect } from "react";
import "@assets/js/common/table";
import ThemeLayoutSelectionModal from "@pages/design-management/page-design/components/ThemeLayoutSelectionModal.jsx";
import designApi from "@services/designApi.jsx";
import themeLayoutMappingApi from "@services/themeLayoutMappingApi.jsx";
import ResourceUploadField from "@components/common/ResourceUploadField.jsx";

const LayoutSetting = ({ themeId, themePageMaster }) => {

    const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;

    // 레이아웃 페이지 상태
    const [layoutPage, setLayoutPage] = useState({
        themePageMaster: themePageMaster,
        languageCode: "ko",
        messageText: "",
        messageColor: "#FFFFFF",
        timerSeconds: 10,
        timerTimeoutRedirectUrl: "start",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 테마 레이아웃 매핑 데이터 상태
    const [themeLayoutMappings, setThemeLayoutMappings] = useState([]);
    const [availableLayouts, setAvailableLayouts] = useState([]);
    const [layoutFilter, setLayoutFilter] = useState("all");
    const [selectAll, setSelectAll] = useState(false);

    // 모달 상태 관리
    const [showThemeLayoutModal, setShowThemeLayoutModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [editTargetMapping, setEditTargetMapping] = useState(null);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [currentLayout, setCurrentLayout] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // API 호출로 데이터 가져오기
    useEffect(() => {
        const fetchLayoutPage = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await designApi.getLayoutPage(themeId);

                setLayoutPage({
                    ...layoutPage,
                    ...data,
                });

                console.log("레이아웃 페이지 데이터 로드 성공:", data);
            } catch (err) {
                console.error("레이아웃 페이지 데이터 로드 실패:", err);
                setError(err);
                // 에러 발생 시 기본 데이터 유지
            } finally {
                setIsLoading(false);
            }
        };

        const fetchThemeLayoutMappings = async () => {
            try {
                const mappings =
                    await themeLayoutMappingApi.getThemeLayoutMappings(themeId);
                setThemeLayoutMappings(mappings);
                console.log("테마 레이아웃 매핑 로드 성공:", mappings);
            } catch (err) {
                console.error("테마 레이아웃 매핑 로드 실패:", err);
            }
        };

        const fetchAvailableLayouts = async () => {
            try {
                const layouts =
                    await themeLayoutMappingApi.getAvailableLayouts(themeId);
                setAvailableLayouts(layouts);
                console.log("사용 가능한 레이아웃 로드 성공:", layouts);
            } catch (err) {
                console.error("사용 가능한 레이아웃 로드 실패:", err);
            }
        };

        fetchLayoutPage();
        fetchThemeLayoutMappings();
        fetchAvailableLayouts();
    }, [themeId]);

    // 입력값 변경 핸들러
    const handleInputChange = (field, value) => {
        setLayoutPage((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // 리소스 업로드 처리 함수
    const handleResourceUploaded = (resourceType, resourceId) => {
        setLayoutPage({
            ...layoutPage,
            [resourceType]: resourceId,
        });
    };

    // 타이머 조절 핸들러
    const handleTimerDecrease = () => {
        setLayoutPage((prev) => ({
            ...prev,
            timerSeconds: Math.max((prev.timerSeconds || 10) - 1, 1),
        }));
    };

    const handleTimerIncrease = () => {
        setLayoutPage((prev) => ({
            ...prev,
            timerSeconds: (prev.timerSeconds || 10) + 1,
        }));
    };

    // 체크박스 선택 핸들러
    const handleCheckboxChange = (id) => {
        setThemeLayoutMappings((prevMappings) =>
            prevMappings.map((mapping) =>
                mapping.id === id
                    ? { ...mapping, selected: !mapping.selected }
                    : mapping
            )
        );
    };

    // 전체 선택 핸들러
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        setThemeLayoutMappings((prevMappings) =>
            prevMappings.map((mapping) => ({
                ...mapping,
                selected: !selectAll,
            }))
        );
    };

    // 레이아웃 추가 핸들러
    const handleAddLayout = (e) => {
        e.preventDefault();
        setIsEditMode(false);
        setEditTargetMapping(null);
        setShowThemeLayoutModal(true);
        document.body.classList.add("modal-open");
    };

    // 모달 닫기 핸들러
    const handleCloseModal = () => {
        setShowThemeLayoutModal(false);
        setIsEditMode(false);
        setEditTargetMapping(null);
        document.body.classList.remove("modal-open");
    };

    // 새 레이아웃 저장 핸들러
    const handleSaveLayout = async (result) => {
        try {
            // 데이터 다시 로드
            const mappings =
                await themeLayoutMappingApi.getThemeLayoutMappings(themeId);
            setThemeLayoutMappings(mappings);

            const layouts =
                await themeLayoutMappingApi.getAvailableLayouts(themeId);
            setAvailableLayouts(layouts);

            alert("레이아웃이 성공적으로 추가/수정되었습니다.");
        } catch (error) {
            console.error("데이터 새로고침 실패:", error);
        }
    };

    // 레이아웃 수정 핸들러
    const handleEditLayout = (mapping) => {
        setEditTargetMapping(mapping);
        setIsEditMode(true);
        setShowThemeLayoutModal(true);
        document.body.classList.add("modal-open");
    };

    // 레이아웃 삭제 핸들러
    const handleDeleteLayout = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
        document.body.classList.add("modal-open");
    };

    // 삭제 모달 닫기 핸들러
    const handleCloseDeleteModal = () => {
        setDeleteTargetId(null);
        setShowDeleteModal(false);
        document.body.classList.remove("modal-open");
    };

    // 삭제 확인 핸들러
    const handleConfirmDelete = async () => {
        if (deleteTargetId) {
            try {
                await themeLayoutMappingApi.removeLayoutFromTheme(
                    deleteTargetId
                );

                // 데이터 다시 로드
                const mappings =
                    await themeLayoutMappingApi.getThemeLayoutMappings(themeId);
                setThemeLayoutMappings(mappings);

                const layouts =
                    await themeLayoutMappingApi.getAvailableLayouts(themeId);
                setAvailableLayouts(layouts);

                alert("레이아웃이 성공적으로 삭제되었습니다.");
                handleCloseDeleteModal();
            } catch (error) {
                console.error("레이아웃 삭제 실패:", error);
                alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
            }
        }
    };

    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (layout) => {
        setCurrentLayout(layout);
        setShowDetailModal(true);
    };

    // 상세정보 모달 닫기 핸들러
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
    };

    // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
    useEffect(() => {
        const eventName = `savePageDesign_layout`; // 각 컴포넌트마다 고유한 이벤트 이름

        const saveHandler = async (event) => {
            try {
                // 저장 중임을 표시
                setIsLoading(true);

                // 자체 로직을 사용하여 데이터 저장
                const isSucceed = await designApi.saveLayoutPage(
                    event.detail.themeId,
                    layoutPage
                );

                if (isSucceed) {
                    // 성공 메시지
                    alert("저장되었습니다.");
                } else {
                    // 실패 메시지
                    alert("저장에 실패했습니다.");
                }
            } catch (error) {
                // 오류 처리
                console.error("저장 실패:", error);
                alert("저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        // 이벤트 리스너 등록
        document.addEventListener(eventName, saveHandler);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [layoutPage, themeId]); // 의존성 배열에 필요한 상태 포함

    // 테이블 초기화
    useEffect(() => {
        if (window.tableManager) {
            window.tableManager.initTable({
                tableSelector: ".common-table",
                filterSelector: "#layout-filter",
                columnWidthOptions: {
                    equalWidth: false,
                    firstColumnWidth: 5,
                    lastColumnWidth: 5,
                    fixedColumns: {
                        1: 10, // NO 열
                        2: 20, // 레이아웃명 열
                        3: 10, // 타입 열
                        4: 10, // 방향 열
                        5: 10, // 상세정보 열
                        6: 10, // 수정 열
                        7: 10, // 삭제 열
                    },
                },
            });
        }
    }, []);

    return (
        <div className="design-settings">
            {/* 언어 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">언어 선택</h4>
                <div className="option-select-container">
                    <select
                        className="option-select"
                        value={layoutPage.languageCode || "ko"}
                        onChange={(e) =>
                            handleInputChange("languageCode", e.target.value)
                        }
                    >
                        <option value="ko">한국어</option>
                        <option value="en">영어</option>
                        <option value="jp">일본어</option>
                        <option value="cn">중국어</option>
                    </select>
                    <i className="ri-arrow-down-s-line select-arrow"></i>
                </div>
            </div>

            {/* 배경 이미지 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 이미지</h4>
                <ResourceUploadField
                    label=""
                    resourceId={layoutPage.backgroundResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundResourceId", id)
                    }
                    acceptTypes="image/*"
                />
            </div>

            {/* 배경 음악 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 음악</h4>
                <ResourceUploadField
                    label=""
                    resourceId={layoutPage.backgroundMusicResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundMusicResourceId", id)
                    }
                    acceptTypes="audio/*"
                />
            </div>

            {/* 1+1 뱃지 이미지 (4컷) 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">1+1 뱃지 이미지 (4컷)</h4>
                <ResourceUploadField
                    label=""
                    resourceId={layoutPage.badgeImage4cutResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("badgeImage4cutResourceId", id)
                    }
                    acceptTypes="image/*"
                />
            </div>

            {/* 1+1 뱃지 이미지 (3컷) 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">1+1 뱃지 이미지 (3컷)</h4>
                <ResourceUploadField
                    label=""
                    resourceId={layoutPage.badgeImage3cutResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("badgeImage3cutResourceId", id)
                    }
                    acceptTypes="image/*"
                />
            </div>

            {/* 문구 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">문구</h4>
                <div className="settings-row">
                    <div className="settings-input">
                        <input
                            type="text"
                            placeholder="문구를 입력해주세요"
                            className="text-input"
                            value={layoutPage.messageText || ""}
                            onChange={(e) =>
                                handleInputChange("messageText", e.target.value)
                            }
                        />
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">색상</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={layoutPage.messageColor || "#FFFFFF"}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={layoutPage.messageColor || "#FFFFFF"}
                            onChange={(e) =>
                                handleInputChange(
                                    "messageColor",
                                    e.target.value
                                )
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 타이머 설정 섹션 */}
            {/* <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button
                            className="timer-btn"
                            onClick={handleTimerDecrease}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input
                            type="text"
                            className="timer-input"
                            value={layoutPage.timerSeconds || 10}
                            readOnly
                        />
                        <button
                            className="timer-btn"
                            onClick={handleTimerIncrease}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select
                            className="page-select"
                            value={
                                layoutPage.timerTimeoutRedirectUrl || "start"
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "timerTimeoutRedirectUrl",
                                    e.target.value
                                )
                            }
                        >
                            <option value="start">시작화면</option>
                            <option value="angle">앵글선택</option>
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div> */}

            {/* 레이아웃 데이터 섹션 헤더 */}
            <div className="data-section-header">
                <div className="data-section-title">
                    <h4 className="section-title">레이아웃</h4>
                    <div className="section-title-icon">
                        <a
                            href="#"
                            className="layout-add-btn"
                            title="레이아웃 추가"
                            onClick={handleAddLayout}
                        >
                            <i className="ri-add-circle-line"></i>
                        </a>
                    </div>
                </div>
                <div className="filter-dropdown-container">
                    <div className="filter-dropdown">
                        <select
                            id="layout-filter"
                            value={layoutFilter}
                            onChange={(e) => setLayoutFilter(e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="landscape">가로</option>
                            <option value="portrait">세로</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* 데이터 테이블 섹션 */}
            <div className="common-table-container">
                <table className="common-table">
                    <thead>
                        <tr>
                            <th>
                                <div className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        id="select-all"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                    <label
                                        htmlFor="select-all"
                                        className="custom-checkbox"
                                    ></label>
                                </div>
                            </th>
                            <th>NO</th>
                            <th>레이아웃명</th>
                            <th>타입</th>
                            <th>방향</th>
                            <th>상세정보</th>
                            <th>수정</th>
                            <th>삭제</th>
                        </tr>
                    </thead>
                    <tbody>
                        {themeLayoutMappings.map((mapping) => (
                            <tr key={mapping.id}>
                                <td>
                                    <div className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            id={`mapping-${mapping.id}`}
                                            checked={mapping.selected || false}
                                            onChange={() =>
                                                handleCheckboxChange(mapping.id)
                                            }
                                        />
                                        <label
                                            htmlFor={`mapping-${mapping.id}`}
                                            className="custom-checkbox"
                                        ></label>
                                    </div>
                                </td>
                                <td>{mapping.displayOrder || mapping.id}</td>
                                <td>{mapping.layoutName}</td>
                                <td>
                                    {mapping.layoutTypeDescription ||
                                        mapping.layoutType}
                                </td>
                                <td>
                                    {mapping.layoutDirectionDescription ||
                                        mapping.layoutDirection}
                                </td>
                                <td>
                                    <button
                                        className="info-btn"
                                        onClick={() => handleInfoClick(mapping)}
                                    >
                                        <i className="ri-information-line"></i>
                                    </button>
                                </td>
                                <td>
                                    <button
                                        className="edit-btn"
                                        onClick={() =>
                                            handleEditLayout(mapping)
                                        }
                                    >
                                        <i className="ri-pencil-line"></i>
                                    </button>
                                </td>
                                <td>
                                    <button
                                        className="delete-btn"
                                        onClick={() =>
                                            handleDeleteLayout(mapping.id)
                                        }
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 테마 레이아웃 선택 모달 */}
            {showThemeLayoutModal && (
                <ThemeLayoutSelectionModal
                    themeId={themeId}
                    availableLayouts={availableLayouts}
                    onClose={handleCloseModal}
                    onSave={handleSaveLayout}
                    isEditMode={isEditMode}
                    initialData={editTargetMapping}
                />
            )}

            {/* 삭제 확인 모달 */}
            {showDeleteModal && (
                <div className="modal-overlay active">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">레이아웃 삭제</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseDeleteModal}
                            >
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>이 레이아웃을 삭제하시겠습니까?</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCloseDeleteModal}
                            >
                                취소
                            </button>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleConfirmDelete}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 수정 모달 */}

            {/* 상세정보 모달 */}
            {showDetailModal && currentLayout && (
                <div className="modal-overlay active" id="layout-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">레이아웃 상세정보</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseDetailModal}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">레이아웃명</span>
                                <span className="detail-content">
                                    {currentLayout.layoutName}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">타입</span>
                                <span className="detail-content">
                                    {currentLayout.layoutTypeDescription ||
                                        currentLayout.layoutType}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">방향</span>
                                <span className="detail-content">
                                    {currentLayout.layoutDirectionDescription ||
                                        currentLayout.layoutDirection}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">표시 순서</span>
                                <span className="detail-content">
                                    {currentLayout.displayOrder}
                                </span>
                            </div>
                            {currentLayout.layoutDataResourceId && (
                                <div className="detail-item-container">
                                    <span className="detail-label">
                                        레이아웃 데이터
                                    </span>
                                    <div className="layout-data">
                                        <img
                                            src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentLayout.layoutDataResourceId}`}
                                            alt="layout_data"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="detail-item-container">
                                <div className="detail-row">
                                    <div className="detail-column">
                                        <span className="detail-label">
                                            테마별 기본 이미지
                                        </span>
                                        <div className="modal-data-image">
                                            {currentLayout.customDefaultImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentLayout.customDefaultImageResourceId}`}
                                                    alt="custom_default"
                                                />
                                            ) : (
                                                <img
                                                    src="images/layoutData/default_layout.png"
                                                    alt="default_image"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="detail-column">
                                        <span className="detail-label">
                                            테마별 호버 이미지
                                        </span>
                                        <div className="modal-data-image">
                                            {currentLayout.customHoverImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentLayout.customHoverImageResourceId}`}
                                                    alt="custom_hover"
                                                />
                                            ) : (
                                                <img
                                                    src="images/layoutData/default_layout_hover.png"
                                                    alt="default_hover"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleCloseDetailModal}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LayoutSetting;
